import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import AddIcon from '@material-ui/icons/Add';
import CheckIcon from '@material-ui/icons/Check';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import VisibilityIcon from '@material-ui/icons/Visibility';
import useDebouncedValue from '../hooks/useDebouncedValue';
import { MERCHANT_CREATE_RESET } from '../constants/merchantConstants';
import {
  createMerchant,
  listMerchants,
  approveMerchant,
  toggleMerchantStatus,
  updateMerchant,
  deleteMerchant,
  deleteManyMerchants,
} from '../actions/merchantActions';

const statusColors = {
  active: 'bg-green-100 text-green-700',
  pending: 'bg-yellow-100 text-yellow-700',
  inactive: 'bg-gray-100 text-gray-500',
};

function StatusBadge({ status }) {
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${statusColors[status] || 'bg-gray-100 text-gray-500'}`}>
      {status || '—'}
    </span>
  );
}

function Toggle({ checked, disabled, onChange }) {
  return (
    <label className="relative inline-flex items-center cursor-pointer">
      <input
        type="checkbox"
        className="sr-only peer"
        checked={checked}
        disabled={disabled}
        onChange={e => onChange(e.target.checked)}
      />
      <div className="w-9 h-5 bg-gray-200 rounded-full peer peer-checked:bg-blue-600 peer-disabled:opacity-40 after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full" />
    </label>
  );
}

function Modal({ open, onClose, title, children, footer, maxWidth = 'max-w-2xl' }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className={`bg-white rounded-xl shadow-xl w-full ${maxWidth} max-h-[90vh] flex flex-col`} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>
        <div className="px-6 py-4 overflow-y-auto flex-1">{children}</div>
        {footer && (
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">{footer}</div>
        )}
      </div>
    </div>
  );
}

const inputCls = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white';
const inputErrorCls = 'border-red-400 focus:ring-red-500';
const btnPrimary = 'bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors';
const btnOutline = 'border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium px-4 py-2 rounded-lg transition-colors';
const iconBtn = 'inline-flex items-center justify-center w-7 h-7 rounded-md border transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
const iconSize = { fontSize: 16 };

const validateCreateMerchant = values => {
  const errors = {};
  const name = values.name.trim();
  const qrNumber = String(values.qrNumber).trim();
  const contactNumber = values.contactNumber.trim();
  const email = values.email.trim();
  const pincode = values.pincode.trim();

  if (!name) errors.name = 'Name is required.';
  else if (!/^[A-Za-z][A-Za-z\s.'-]{1,48}$/.test(name)) errors.name = 'Name should contain only letters.';

  if (!qrNumber) errors.qrNumber = 'QR number is required.';
  else if (!/^\d+$/.test(qrNumber)) errors.qrNumber = 'QR number should contain only digits.';

  if (!contactNumber) errors.contactNumber = 'Contact number is required.';
  else if (!/^\d{10}$/.test(contactNumber)) errors.contactNumber = 'Contact number must be 10 digits.';

  if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = 'Enter a valid email address.';

  if (pincode && !/^\d{6}$/.test(pincode)) errors.pincode = 'Pincode must be 6 digits.';

  const upiId = values.upiId.trim();
  if (upiId && !/^[\w.\-]{2,256}@[a-zA-Z]{2,64}$/.test(upiId)) errors.upiId = 'Enter a valid UPI ID (e.g. name@bank).';

  return errors;
};

const FieldError = ({ message }) => (
  message ? <p className="mt-1 text-xs text-red-600">{message}</p> : null
);

export default function MerchantCreate() {
  const dispatch = useDispatch();
  const { userInfo } = useSelector(s => s.userLogin || {});
  const { getRoleData } = useSelector(s => s.getRoleInfo || {});
  const role = getRoleData || userInfo?.role || {};
  const canViewPage = !!role.merchantPageView;
  const canViewMerchant = !!role.merchantView;
  const canAddMerchant = !!role.merchantAdd;
  const canEditMerchant = !!role.merchantEdit;
  const canToggleMerchant = !!role.merchantToggle;
  const canDeleteMerchant = !!role.merchantDelete;

  const [q, setQ] = useState('');
  const [status, setStatus] = useState('all');
  const [limit, setLimit] = useState(10);
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingMerchant, setEditingMerchant] = useState(null);
  const [form, setForm] = useState({
    name: '', qrNumber: '', contactNumber: '',
    email: '', location: '', pincode: '', upiId: '', status: 'pending', isSystemMerchant: false,
  });
  const [formTouched, setFormTouched] = useState({});
  const [editForm, setEditForm] = useState({
    name: '', identifier: '', contactNumber: '', email: '', location: '', pincode: '', upiId: '', status: 'pending', isSystemMerchant: false,
  });
  const [systemMerchantAlert, setSystemMerchantAlert] = useState('');
  const [selectedRows, setSelectedRows] = useState([]);

  const { loading, data } = useSelector(s => s.merchantCreate);
  const {
    items = [],
    loading: listLoading,
    page = 1,
    pages = 1,
    total = 0,
    systemMerchant = null,
  } = useSelector(s => s.merchantList);

  const debouncedQ = useDebouncedValue(q, 400);
  useEffect(() => {
    if (canViewPage) {
      setSelectedRows([]);
      dispatch(listMerchants({ page: 1, limit, q: debouncedQ, status }));
    }
  }, [dispatch, limit, debouncedQ, status, canViewPage]);

  useEffect(() => {
    if (data?.merchant?._id) {
      dispatch(listMerchants({ page: 1, limit, q, status }));
      setSelectedRows([]);
      setOpen(false);
      setForm({ name: '', qrNumber: '', contactNumber: '', email: '', location: '', pincode: '', upiId: '', status: 'pending', isSystemMerchant: false });
      setFormTouched({});
      setSystemMerchantAlert('');
    }
  }, [data]);

  const formErrors = validateCreateMerchant(form);
  const showError = field => formTouched[field] ? formErrors[field] : '';

  const submit = async e => {
    e.preventDefault();
    if (!canAddMerchant) return;
    const errors = validateCreateMerchant(form);
    if (Object.keys(errors).length) {
      setFormTouched({
        name: true,
        qrNumber: true,
        contactNumber: true,
        email: true,
        pincode: true,
      });
      return;
    }

    try {
      await dispatch(createMerchant({
        ...form,
        name: form.name.trim(),
        qrNumber: Number(String(form.qrNumber).trim()),
        contactNumber: form.contactNumber.trim(),
        email: form.email.trim(),
        pincode: form.pincode.trim(),
      }));
    } catch (err) {
      if (form.isSystemMerchant) setSystemMerchantAlert(err.message || 'A system merchant is already defined.');
      toast(err.message || 'Merchant create failed', { type: 'error', hideProgressBar: true, autoClose: 2500 });
    }
  };

  const handleApprove = async id => {
    if (!canEditMerchant) return;
    try {
      await dispatch(approveMerchant(id));
      toast('Merchant approved', { type: 'success', hideProgressBar: true, autoClose: 2000 });
      dispatch(listMerchants({ page: 1, limit, q, status }));
    } catch (err) {
      toast(err.response?.data?.message || err.message || 'Merchant approval failed', { type: 'error', hideProgressBar: true, autoClose: 2500 });
    }
  };

  const handleActiveSwitch = async (m, nextChecked) => {
    if (!canToggleMerchant) return;
    if (m.status === 'pending') return;
    if (nextChecked !== (m.status === 'active')) {
      await dispatch(toggleMerchantStatus(m._id));
      setSelectedRows([]);
      dispatch(listMerchants({ page, limit, q, status }));
    }
  };

  const handlePageChange = p => {
    setSelectedRows([]);
    dispatch(listMerchants({ page: p, limit, q, status }));
  };

  const f = (field, val) => {
    setForm(prev => ({ ...prev, [field]: val }));
  };
  const ef = (field, val) => setEditForm(prev => ({ ...prev, [field]: val }));

  const systemMerchantMessage = merchant =>
    `System merchant "${merchant.name}" is already defined. Disable its System Merchant toggle or delete it before selecting another merchant.`;

  const changeSystemMerchant = (mode, checked) => {
    const editingId = mode === 'edit' ? editingMerchant?._id : null;
    if (checked && systemMerchant && systemMerchant._id !== editingId) {
      setSystemMerchantAlert(systemMerchantMessage(systemMerchant));
      return;
    }
    setSystemMerchantAlert('');
    if (mode === 'edit') ef('isSystemMerchant', checked);
    else f('isSystemMerchant', checked);
  };

  const openEdit = merchant => {
    setEditingMerchant(merchant);
    setEditForm({
      name: merchant.name || '',
      identifier: merchant.identifier || '',
      qrNumber: merchant.qrNumber ?? '',
      contactNumber: merchant.contactNumber || '',
      email: merchant.email || '',
      location: merchant.location || '',
      pincode: merchant.pincode || '',
      upiId: merchant.upiId || '',
      status: merchant.status || 'pending',
      isSystemMerchant: merchant.isSystemMerchant === true,
    });
    setSystemMerchantAlert('');
    setEditOpen(true);
  };

  const submitEdit = async e => {
    e.preventDefault();
    if (!canEditMerchant || !editingMerchant?._id) return;
    try {
      const payload = { ...editForm };
      if (!String(payload.identifier || '').trim()) delete payload.identifier;
      else payload.identifier = String(payload.identifier).trim();
      await dispatch(updateMerchant(editingMerchant._id, payload));
      toast('Merchant updated', { type: 'success', hideProgressBar: true, autoClose: 2000 });
      setEditOpen(false);
      setEditingMerchant(null);
      setSelectedRows([]);
      dispatch(listMerchants({ page, limit, q, status }));
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Merchant update failed';
      if (editForm.isSystemMerchant) setSystemMerchantAlert(message);
      toast(message, { type: 'error', hideProgressBar: true, autoClose: 2500 });
    }
  };

  const handleDelete = async merchant => {
    if (!canDeleteMerchant) return;
    if (!window.confirm(`Delete merchant "${merchant.name}"? This will release the linked QR.`)) return;
    try {
      await dispatch(deleteMerchant(merchant._id));
      toast('Merchant deleted', { type: 'success', hideProgressBar: true, autoClose: 2000 });
      setSelectedRows(prev => prev.filter(id => id !== merchant._id));
      dispatch(listMerchants({ page: 1, limit, q, status }));
    } catch (err) {
      toast(err.response?.data?.message || err.message || 'Merchant delete failed', { type: 'error', hideProgressBar: true, autoClose: 2500 });
    }
  };

  const visibleIds = items.map(item => item._id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selectedRows.includes(id));
  const someVisibleSelected = visibleIds.some(id => selectedRows.includes(id));

  const toggleSelectAll = checked => {
    setSelectedRows(prev => {
      if (checked) return Array.from(new Set([...prev, ...visibleIds]));
      return prev.filter(id => !visibleIds.includes(id));
    });
  };

  const toggleSelectRow = (id, checked) => {
    setSelectedRows(prev => checked
      ? Array.from(new Set([...prev, id]))
      : prev.filter(rowId => rowId !== id)
    );
  };

  const handleBulkDelete = async () => {
    if (!canDeleteMerchant || !selectedRows.length) return;
    if (!window.confirm(`Delete ${selectedRows.length} selected merchant(s)? This will release their linked QR codes.`)) return;
    try {
      await dispatch(deleteManyMerchants(selectedRows));
      toast(`${selectedRows.length} merchant(s) deleted`, { type: 'success', hideProgressBar: true, autoClose: 2000 });
      setSelectedRows([]);
      dispatch(listMerchants({ page: 1, limit, q, status }));
    } catch (err) {
      toast(err.response?.data?.message || err.message || 'Bulk delete failed', { type: 'error', hideProgressBar: true, autoClose: 2500 });
    }
  };

  useEffect(() => {
    return () => {
      dispatch({ type: MERCHANT_CREATE_RESET });
    };
  }, [dispatch]);

  if (!canViewPage) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="bg-white border border-gray-200 rounded-xl p-6 max-w-lg">
          <h1 className="text-xl font-semibold text-gray-800 mb-2">Not authorized</h1>
          <p className="text-sm text-gray-500">You do not have permission to view merchants.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold text-gray-800">Merchants</h1>
        <div className="flex items-center gap-2">
          {canDeleteMerchant && selectedRows.length > 0 && (
            <button onClick={handleBulkDelete} className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
              <DeleteIcon style={{ fontSize: 16 }} />
              <span>Delete ({selectedRows.length})</span>
            </button>
          )}
          {canAddMerchant && (
            <button onClick={() => { setSystemMerchantAlert(''); setOpen(true); }} className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
              <AddIcon className="w-4 h-4" />
              <span>Add Merchant</span>
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-4 flex flex-wrap gap-3 items-center">
        <input
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Search by name or identifier"
          className={`${inputCls} flex-1 min-w-[200px]`}
        />
        <select value={status} onChange={e => setStatus(e.target.value)} className={inputCls} style={{ width: 'auto' }}>
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <span className="text-sm text-gray-500">Total: {total}</span>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                {canDeleteMerchant && (
                  <th className="px-4 py-3 text-left bg-gray-50 border-b border-gray-200 w-10">
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      ref={input => {
                        if (input) input.indeterminate = !allVisibleSelected && someVisibleSelected;
                      }}
                      onChange={e => toggleSelectAll(e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                      aria-label="Select all merchants"
                    />
                  </th>
                )}
                {['S.No', 'Name', 'Identifier', 'QR Number', 'QR Token', 'Location', 'Status', 'Active?', 'Created At', 'Action'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {listLoading ? (
                <tr>
                  <td colSpan={canDeleteMerchant ? 11 : 10} className="px-4 py-12 text-center">
                    <div className="flex justify-center"><div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" /></div>
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={canDeleteMerchant ? 11 : 10} className="px-4 py-12 text-center text-gray-400 text-sm">No merchants found</td>
                </tr>
              ) : items.map((m, index) => (
                <tr key={m._id} className="hover:bg-gray-50 transition-colors">
                  {canDeleteMerchant && (
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedRows.includes(m._id)}
                        onChange={e => toggleSelectRow(m._id, e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                        aria-label={`Select ${m.name}`}
                      />
                    </td>
                  )}
                  <td className="px-4 py-3 text-sm text-gray-500">{(page - 1) * limit + index + 1}</td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-800">
                    <div className="flex items-center gap-2">
                      <span>{m.name}</span>
                      {m.isSystemMerchant && (
                        <span className="rounded-full border border-purple-200 bg-purple-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-purple-700">
                          System
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm font-mono text-gray-600">{m.identifier ?? '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{m.qrNumber ?? '—'}</td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-mono text-gray-500 block max-w-[140px] truncate" title={m.qrToken}>{m.qrToken ?? '—'}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{m.location ?? '—'}</td>
                  <td className="px-4 py-3"><StatusBadge status={m.status} /></td>
                  <td className="px-4 py-3">
                    <Toggle
                      checked={m.status === 'active'}
                      disabled={m.status === 'pending' || !canToggleMerchant}
                      onChange={checked => handleActiveSwitch(m, checked)}
                    />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {m.createdAt ? new Date(m.createdAt).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      {m.status !== 'pending' && canViewMerchant && (
                        <Link
                          to={`/admin/merchants/${m.qrToken}`}
                          className={`${iconBtn} text-blue-600 border-blue-200 hover:border-blue-400 hover:bg-blue-50`}
                          title="View merchant"
                          aria-label={`View ${m.name}`}
                        >
                          <VisibilityIcon style={iconSize} />
                        </Link>
                      )}
                      {m.status === 'pending' && canEditMerchant && (
                        <button
                          onClick={() => handleApprove(m._id)}
                          disabled={listLoading || m.qrNumber === undefined || m.qrNumber === null}
                          className={`${iconBtn} text-green-700 border-green-200 hover:border-green-400 hover:bg-green-50`}
                          title={m.qrNumber === undefined || m.qrNumber === null ? 'Assign a QR number before approval' : 'Approve merchant'}
                          aria-label={`Approve ${m.name}`}
                        >
                          <CheckIcon style={iconSize} />
                        </button>
                      )}
                      {canEditMerchant && (
                        <button
                          onClick={() => openEdit(m)}
                          className={`${iconBtn} text-gray-700 border-gray-200 hover:border-gray-400 hover:bg-gray-50`}
                          title="Edit merchant"
                          aria-label={`Edit ${m.name}`}
                        >
                          <EditIcon style={iconSize} />
                        </button>
                      )}
                      {canDeleteMerchant && (
                        <button
                          onClick={() => handleDelete(m)}
                          className={`${iconBtn} text-red-600 border-red-200 hover:border-red-400 hover:bg-red-50`}
                          title="Delete merchant"
                          aria-label={`Delete ${m.name}`}
                        >
                          <DeleteIcon style={iconSize} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500">{total} total</span>
            <select value={limit} onChange={e => setLimit(Number(e.target.value))}
              className="border border-gray-300 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
              {[10, 20, 50].map(n => <option key={n} value={n}>{n}/page</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => handlePageChange(page - 1)} disabled={page <= 1}
              className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors">
              Prev
            </button>
            <span className="text-sm text-gray-600 px-1">{page} / {pages || 1}</span>
            <button onClick={() => handlePageChange(page + 1)} disabled={page >= pages}
              className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors">
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Create Modal */}
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Onboard Merchant"
        footer={
          <>
            <button onClick={() => setOpen(false)} className={btnOutline}>Cancel</button>
            <button onClick={submit} disabled={loading} className={btnPrimary}>
              {loading ? 'Saving…' : 'Save Merchant'}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input
                value={form.name}
                onChange={e => f('name', e.target.value)}
                onBlur={() => setFormTouched(prev => ({ ...prev, name: true }))}
                className={`${inputCls} ${showError('name') ? inputErrorCls : ''}`}
                placeholder="Team member name"
                aria-invalid={!!showError('name')}
              />
              <FieldError message={showError('name')} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">QR Number *</label>
              <input
                value={form.qrNumber}
                onChange={e => f('qrNumber', e.target.value.replace(/\D/g, ''))}
                onBlur={() => setFormTouched(prev => ({ ...prev, qrNumber: true }))}
                className={`${inputCls} ${showError('qrNumber') ? inputErrorCls : ''}`}
                placeholder="e.g. 1001"
                aria-invalid={!!showError('qrNumber')}
              />
              <FieldError message={showError('qrNumber')} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact Number *</label>
              <input
                value={form.contactNumber}
                onChange={e => f('contactNumber', e.target.value.replace(/\D/g, '').slice(0, 10))}
                onBlur={() => setFormTouched(prev => ({ ...prev, contactNumber: true }))}
                className={`${inputCls} ${showError('contactNumber') ? inputErrorCls : ''}`}
                placeholder="Contact number"
                inputMode="numeric"
                maxLength={10}
                aria-invalid={!!showError('contactNumber')}
              />
              <FieldError message={showError('contactNumber')} />
            </div>
            <div className="flex items-end">
              <div className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-2">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-semibold text-gray-800">System Merchant</p>
                    <p className="text-xs text-gray-500">Only one merchant can have this role.</p>
                  </div>
                  <Toggle
                    checked={form.isSystemMerchant}
                    onChange={checked => changeSystemMerchant('create', checked)}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email (optional)</label>
              <input
                value={form.email}
                onChange={e => f('email', e.target.value)}
                onBlur={() => setFormTouched(prev => ({ ...prev, email: true }))}
                className={`${inputCls} ${showError('email') ? inputErrorCls : ''}`}
                placeholder="Email"
                type="email"
                aria-invalid={!!showError('email')}
              />
              <FieldError message={showError('email')} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
              <input value={form.location} onChange={e => f('location', e.target.value)} className={inputCls} placeholder="Location" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pincode</label>
              <input
                value={form.pincode}
                onChange={e => f('pincode', e.target.value.replace(/\D/g, '').slice(0, 6))}
                onBlur={() => setFormTouched(prev => ({ ...prev, pincode: true }))}
                className={`${inputCls} ${showError('pincode') ? inputErrorCls : ''}`}
                placeholder="Pincode"
                inputMode="numeric"
                maxLength={6}
                aria-invalid={!!showError('pincode')}
              />
              <FieldError message={showError('pincode')} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">UPI ID</label>
              <input
                value={form.upiId}
                onChange={e => f('upiId', e.target.value)}
                onBlur={() => setFormTouched(prev => ({ ...prev, upiId: true }))}
                className={`${inputCls} ${showError('upiId') ? inputErrorCls : ''}`}
                placeholder="e.g. name@bank"
                aria-invalid={!!showError('upiId')}
              />
              <FieldError message={showError('upiId')} />
            </div>
          </div>
          {systemMerchantAlert && (
            <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <p className="font-semibold">System merchant already defined</p>
              <p className="mt-1">{systemMerchantAlert}</p>
            </div>
          )}
        </div>
      </Modal>

      {/* Edit Modal */}
      <Modal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title="Edit Merchant"
        footer={
          <>
            <button onClick={() => setEditOpen(false)} className={btnOutline}>Cancel</button>
            <button onClick={submitEdit} className={btnPrimary}>Update Merchant</button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input value={editForm.name} onChange={e => ef('name', e.target.value)} className={inputCls} placeholder="Merchant name" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Identifier</label>
              <input
                value={editForm.identifier}
                onChange={e => ef('identifier', e.target.value)}
                className={inputCls}
                placeholder="e.g. MR001"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">QR Number *</label>
              <input
                value={editForm.qrNumber}
                onChange={e => ef('qrNumber', e.target.value.replace(/\D/g, ''))}
                className={`${inputCls} disabled:bg-gray-100 disabled:text-gray-500`}
                placeholder="Enter an available QR number"
                inputMode="numeric"
                disabled={editingMerchant?.qrNumber !== undefined && editingMerchant?.qrNumber !== null}
              />
              <p className="mt-1 text-xs text-gray-400">
                {editingMerchant?.qrNumber !== undefined && editingMerchant?.qrNumber !== null
                  ? 'QR number is already assigned.'
                  : 'Assign an available number from the QR Pool.'}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact Number</label>
              <input value={editForm.contactNumber} onChange={e => ef('contactNumber', e.target.value.replace(/\D/g, '').slice(0, 10))} className={inputCls} placeholder="Contact number" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input value={editForm.email} onChange={e => ef('email', e.target.value)} className={inputCls} placeholder="Email" type="email" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
              <input value={editForm.location} onChange={e => ef('location', e.target.value)} className={inputCls} placeholder="Location" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pincode</label>
              <input value={editForm.pincode} onChange={e => ef('pincode', e.target.value.replace(/\D/g, '').slice(0, 6))} className={inputCls} placeholder="Pincode" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">UPI ID</label>
              <input value={editForm.upiId} onChange={e => ef('upiId', e.target.value)} className={inputCls} placeholder="e.g. name@bank" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select value={editForm.status} onChange={e => ef('status', e.target.value)} className={inputCls}>
                <option value="pending">Pending</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-gray-800">System Merchant</p>
                <p className="mt-0.5 text-xs text-gray-500">Only one merchant can have this role.</p>
              </div>
              <Toggle
                checked={editForm.isSystemMerchant}
                onChange={checked => changeSystemMerchant('edit', checked)}
              />
            </div>
          </div>
          {systemMerchantAlert && (
            <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <p className="font-semibold">System merchant already defined</p>
              <p className="mt-1">{systemMerchantAlert}</p>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}
